import { computed, effect, inject, signal, Service } from '@angular/core';
import { Subscription } from 'rxjs';
import {
  ChecklistItemWriterService,
  PreviousCustomInputPayload,
} from '../../../services/checklist-item-writer.service';
import { ChecklistStoreService } from '../../../services/checklist-store.service';
import { ChecklistLoaderService } from '../../../services/checklist-loader.service';
import {
  ChecklistLifecycleService,
  IsolatedPreviewWriteError,
} from '../../../services/checklist-lifecycle.service';
import { RepeatableMaterializerService } from '../../../services/repeatable-materializer.service';
import { LiveChecklistActionService } from '../../../services/realtime/live-checklist-action.service';
import {
  ChecklistAction,
  ChecklistDetailHubClient,
} from '../../../services/realtime/checklist-detail-hub.client';
import { AuthService } from '../../../services/authentication/auth.service';
import { ChecklistItemOrderPipe } from '../../../pipes/checklist-item-order.pipe';
import { SectionItemOrderPipe } from '../../../pipes/section-item-order.pipe';
import { ChecklistViewer, HubUser } from '../types/generated/checklist-models';
import { controlIdOf } from '../utils/field-ref.util';
import { ChecklistItemStatus } from '../types/ChecklistItemStatus.enum';
import { ChecklistStatus } from '../types/ChecklistStatus.enum';
import { Checklist, ChecklistItem, Section } from '../types/ChecklistTypes';
import { PresenceColorService } from './presence-color.service';
import { ScrollService } from './scroll.service';
import { SectionStateService } from './sectionState.service';
import { ChecklistSelection, StorageService } from './storage.service';
import { findNextUncheckedItem } from '../utils/checklist-auto-advance';
import { getVisibleItemsForSection } from '../utils/checklist-visible-entries';
import {
  ChecklistProgress,
  deriveChecklistProgress,
} from '../utils/checklist-progress';

/** A field id is only unique within its item. */
export function fieldActivityKey(itemId: string, fieldId: string): string {
  return `${itemId}:${fieldId}`;
}

export interface FieldActivitySummary {
  viewers: readonly HubUser[];
  isTyping: boolean;
  draft: string | null;
  lockedBy: HubUser | null;
}

@Service()
export class ChecklistService {
  private readonly itemWriter = inject(ChecklistItemWriterService);
  private readonly store = inject(ChecklistStoreService);
  private readonly lifecycle = inject(ChecklistLifecycleService);
  private readonly materializer = inject(RepeatableMaterializerService);
  private readonly checklistLoader = inject(ChecklistLoaderService);
  private readonly liveChecklistActionService = inject(
    LiveChecklistActionService
  );
  private readonly storageService = inject(StorageService);
  private readonly scrollService = inject(ScrollService);
  private readonly sectionService = inject(SectionStateService);
  private readonly checklistDetailHub = inject(ChecklistDetailHubClient);
  private readonly authService = inject(AuthService);
  private readonly presenceColors = inject(PresenceColorService);

  private liveStatusSubscription: Subscription | null = null;
  private liveStatusChecklistId: string | null = null;

  private readonly state = {
    selectedItemId: signal<string | null>(null),
    currentSectionId: signal<string | null>(null),
    dialogs: {
      completion: signal<boolean>(false),
      discard: signal<boolean>(false),
    },
    hideCompleted: signal<boolean>(
      this.storageService.loadHideCompleted() ?? false
    ),
  };

  sectionItemOrder = new SectionItemOrderPipe();
  checklistItemOrder = new ChecklistItemOrderPipe();

  checklist = this.store.selectedChecklist;
  readonly isIsolatedPreview = computed(() => {
    const checklist = this.checklist();
    return !!checklist && this.store.isIsolatedPreview(checklist.id);
  });
  selectedItem = computed(() => {
    return (
      this.checklist()
        ?.sections.find(section => section.id === this.state.currentSectionId())
        ?.items.find(item => item.id === this.state.selectedItemId()) ?? null
    );
  });

  // True only while the selected item is still the materializing prototype, so task-detail is
  // interactive as soon as selection moves to the synthesized occurrence.
  readonly isMaterializing = computed(() => {
    const proto = this.materializer.materializingPrototype();
    return proto !== null && this.state.selectedItemId() === proto;
  });

  constructor() {
    this.materializer.registerSelectionHandler((sectionId, item) =>
      this.setCurrentSelection(sectionId, item)
    );
    this.presenceColors.connect(
      computed(() =>
        this.checklistDetailHub.state() === 'live'
          ? this.liveRoster().others.map(viewer => viewer.user.userId)
          : null
      )
    );
    // Only this client knows its open task, and mobile portrait clears the selection when the
    // task modal closes, which marks no task.
    effect(() => {
      const itemId = this.state.selectedItemId();
      if (this.checklistDetailHub.state() === 'live') {
        this.checklistDetailHub.setViewedItem(itemId);
      }
    });
  }

  showChecklistCompletionDialog = this.state.dialogs.completion.asReadonly();
  showChecklistDiscardDialog = this.state.dialogs.discard.asReadonly();

  // Render-only and never feeds progress counts.
  readonly hideCompleted = this.state.hideCompleted.asReadonly();

  setHideCompleted(value: boolean): void {
    this.state.hideCompleted.set(value);
    this.storageService.saveHideCompleted(value);
  }

  private readonly liveRoster = computed<{
    own: readonly ChecklistViewer[];
    others: readonly ChecklistViewer[];
  }>(() => {
    const own: ChecklistViewer[] = [];
    const others: ChecklistViewer[] = [];
    if (this.checklistDetailHub.state() !== 'live') return { own, others };

    const ownUserId = this.authService.getProfile()?.userId;
    for (const viewer of this.checklistDetailHub.viewers()) {
      (viewer.user.userId === ownUserId ? own : others).push(viewer);
    }
    return { own, others };
  });

  readonly otherViewers = computed<readonly HubUser[]>(() =>
    this.liveRoster().others.map(viewer => viewer.user)
  );

  readonly otherViewersByItem = computed<
    ReadonlyMap<string, readonly HubUser[]>
  >(() => {
    const byItem = new Map<string, HubUser[]>();
    for (const viewer of this.liveRoster().others) {
      for (const itemId of viewer.itemIds) {
        const onItem = byItem.get(itemId);
        if (onItem) {
          onItem.push(viewer.user);
        } else {
          byItem.set(itemId, [viewer.user]);
        }
      }
    }
    return byItem;
  });

  readonly otherFieldActivityByField = computed<
    ReadonlyMap<string, FieldActivitySummary>
  >(() => {
    const byField = new Map<
      string,
      FieldActivitySummary & { viewers: HubUser[] }
    >();
    const roster = this.liveRoster().others;
    for (const viewer of roster) {
      for (const activity of viewer.fields) {
        const key = fieldActivityKey(
          activity.itemId,
          controlIdOf(activity.field)
        );
        const entry = byField.get(key);
        if (entry) {
          entry.viewers.push(viewer.user);
          entry.isTyping = entry.isTyping || activity.isTyping;
          entry.draft = activity.draft ?? entry.draft ?? null;
        } else {
          byField.set(key, {
            viewers: [viewer.user],
            isTyping: activity.isTyping,
            draft: activity.draft ?? null,
            lockedBy: null,
          });
        }
      }
    }
    for (const viewer of roster) {
      for (const lock of viewer.editLocks) {
        const key = fieldActivityKey(lock.itemId, controlIdOf(lock.field));
        let entry = byField.get(key);
        if (!entry) {
          entry = { viewers: [], isTyping: false, draft: null, lockedBy: null };
          byField.set(key, entry);
        }
        entry.lockedBy = viewer.user;
        if (!entry.viewers.includes(viewer.user))
          entry.viewers.push(viewer.user);
      }
    }
    return byField;
  });

  private readonly ownDraft = signal<{
    itemId: string;
    controlId: string;
    text: string;
  } | null>(null);

  readonly liveDraftsByItem = computed<
    ReadonlyMap<string, ReadonlyMap<string, string>>
  >(() => {
    const byItem = new Map<string, Map<string, string>>();
    const put = (itemId: string, controlId: string, text: string) => {
      const drafts = byItem.get(itemId) ?? new Map<string, string>();
      drafts.set(controlId, text);
      byItem.set(itemId, drafts);
    };
    for (const viewer of this.liveRoster().others) {
      for (const activity of viewer.fields) {
        if (activity.draft != null) {
          put(activity.itemId, controlIdOf(activity.field), activity.draft);
        }
      }
    }
    const own = this.ownDraft();
    if (own) put(own.itemId, own.controlId, own.text);
    return byItem;
  });

  /** Keyed by `fieldActivityKey`, and empty unless the hub is live. */
  readonly ownEditLockKeys = computed<ReadonlySet<string>>(() => {
    const keys = new Set<string>();
    for (const viewer of this.liveRoster().own) {
      for (const lock of viewer.editLocks) {
        keys.add(fieldActivityKey(lock.itemId, controlIdOf(lock.field)));
      }
    }
    return keys;
  });

  /** Scoped to the item `setViewedItem` last reported because the server rejects a field reported against no item. */
  setFieldActivity(
    fieldId: string | null,
    isTyping: boolean,
    draft: string | null = null
  ): void {
    const itemId = this.state.selectedItemId();
    this.ownDraft.set(
      fieldId !== null && isTyping && draft !== null && itemId !== null
        ? { itemId, controlId: fieldId, text: draft }
        : null
    );
    this.checklistDetailHub.setFieldActivity(fieldId, isTyping, draft);
  }

  clearFieldActivity(fieldId: string): void {
    if (this.ownDraft()?.controlId === fieldId) this.ownDraft.set(null);
    this.checklistDetailHub.clearFieldActivity(fieldId);
  }

  readonly checklistStatus = computed(
    () => this.checklist()?.status ?? ChecklistStatus.InProgress
  );
  readonly isChecklistCompleted = computed(
    () => this.checklistStatus() === ChecklistStatus.Completed
  );
  readonly isChecklistDiscarded = computed(
    () => this.checklistStatus() === ChecklistStatus.Discarded
  );

  async loadChecklist(id: string) {
    await this.checklistLoader.loadChecklist(id);
    await this.lifecycle.setSelectedChecklist(id);
    // Selection is left to the checklist component because auto-selecting here would highlight the first row on mobile.
  }

  startLiveStatusUpdates(checklistId: string): void {
    if (this.liveStatusChecklistId === checklistId) {
      return;
    }

    this.liveStatusChecklistId = checklistId;
    this.liveStatusSubscription ??= this.subscribeToLiveStatus();
    void this.checklistDetailHub.connectTo(checklistId);
  }

  stopLiveStatusUpdates(): void {
    if (this.liveStatusChecklistId === null) {
      return;
    }

    this.liveStatusSubscription?.unsubscribe();
    this.liveStatusSubscription = null;
    this.liveStatusChecklistId = null;
    void this.checklistDetailHub.disconnect();
  }

  private subscribeToLiveStatus(): Subscription {
    const subscription = new Subscription();
    subscription.add(
      this.checklistDetailHub.checklistActions$.subscribe(action =>
        this.applyLiveAction(action)
      )
    );
    // Changes during the gap were never pushed, so the checklist has to be re-read over HTTP.
    subscription.add(
      this.checklistDetailHub.reconnected$.subscribe(() => {
        const checklistId = this.liveStatusChecklistId;
        if (checklistId !== null) {
          void this.checklistLoader.loadChecklist(checklistId);
        }
      })
    );
    return subscription;
  }

  private applyLiveAction(action: ChecklistAction): void {
    if (action.checklistId !== this.liveStatusChecklistId) {
      return;
    }

    this.liveChecklistActionService.applyLiveChecklistAction(action);
    if (
      action.type === 'checklistCompleted' ||
      action.type === 'checklistDiscarded'
    ) {
      this.hideCompleteDialog();
      this.hideDiscardDialog();
    }
  }

  private initializeFirstSelection(checklist: Checklist) {
    const lastSelection = this.storageService.getChecklistLastSelection(
      checklist.id
    );
    if (lastSelection === undefined) {
      this.selectFirstAvailableItem(checklist);
    } else {
      const restoredSelection = this.tryRestoreLastSelection(
        checklist,
        lastSelection
      );
      if (!restoredSelection) {
        this.selectFirstAvailableItem(checklist);
      }
    }
    const currentlySelectedItem = this.state.selectedItemId();
    const currentlySelectedSection = this.state.currentSectionId();
    if (currentlySelectedItem && currentlySelectedSection) {
      this.moveToNextItem(
        currentlySelectedSection,
        currentlySelectedItem,
        false
      );
    }
  }

  private tryRestoreLastSelection(
    checklist: Checklist,
    lastSelection: ChecklistSelection
  ) {
    const section = checklist.sections.find(
      s => s.id === lastSelection.sectionId
    );
    const visibleItems = section ? getVisibleItemsForSection(section) : [];
    const storedItem = section?.items.find(i => i.id === lastSelection.itemId);
    const storedGroupId = storedItem?.isRepeatablePrototype
      ? (storedItem.repeatableGroupId ?? storedItem.id)
      : undefined;
    const item =
      visibleItems.find(i => i.id === lastSelection.itemId) ??
      (storedGroupId
        ? visibleItems.find(i => i.repeatableGroupId === storedGroupId)
        : undefined);

    if (section && item) {
      this.setCurrentSelection(section.id, item);
      return true;
    }
    return false;
  }

  private selectFirstAvailableItem(checklist: Checklist) {
    for (const section of this.sectionItemOrder.transform(checklist.sections)) {
      const visibleIds = new Set(
        getVisibleItemsForSection(section).map(item => item.id)
      );
      const firstItem = this.checklistItemOrder
        .transform(section.items ?? [])
        .find(item => visibleIds.has(item.id));
      if (firstItem) {
        this.setCurrentSelection(section.id, firstItem);
        return;
      }
    }
  }

  setCurrentSelection(sectionId: string, item: ChecklistItem) {
    this.state.currentSectionId.set(sectionId);
    this.state.selectedItemId.set(item.id);
    this.saveSelection(sectionId, item.id);
  }

  clearCurrentSelection() {
    this.state.currentSectionId.set(null);
    this.state.selectedItemId.set(null);
  }

  ensureSelection() {
    const checklist = this.checklist();
    if (!checklist || this.selectedItem()) return;
    // Defer so the task-list DOM exists before moveToNextItem scrolls and expands.
    setTimeout(() => {
      if (this.checklist() && !this.selectedItem()) {
        this.initializeFirstSelection(checklist);
      }
    }, 1);
  }

  private saveSelection(sectionId: string, itemId: string) {
    if (
      this.checklistStatus() === ChecklistStatus.InProgress &&
      !this.isIsolatedPreview()
    ) {
      this.storageService.saveChecklistLastSelection(
        this.checklist()?.id ?? '',
        {
          sectionId,
          itemId,
        }
      );
    }
  }

  findSectionForItem(itemId: string): Section | undefined {
    return this.checklist()?.sections.find(section =>
      section.items.some(item => item.id === itemId)
    );
  }

  private moveToNextItem(
    currentSectionId: string,
    currentItemId: string,
    selectNext = false
  ) {
    const checklist = this.checklist();
    if (!checklist) return;

    const flattenedItems = this.sectionItemOrder
      .transform(checklist.sections)
      .flatMap(section =>
        this.checklistItemOrder.transform(
          selectNext ? getVisibleItemsForSection(section) : section.items
        )
      );

    const next = findNextUncheckedItem(flattenedItems, currentItemId);
    const nextSection = next
      ? checklist.sections.find(s => s.id === next.item.sectionId)
      : undefined;
    if (next && nextSection) {
      const { item: nextItem, wrapped } = next;
      if (nextSection.id !== currentSectionId) {
        this.sectionService.requestSectionExpand(nextSection.id);
      }
      if (selectNext) {
        this.setCurrentSelection(nextSection.id, nextItem);
      }
      // Forward scrolls to the item after `nextItem` so it stays previewable, while a wrap scrolls to its target.
      if (wrapped) {
        this.scrollService.scrollToElementById(nextItem.id);
      } else {
        const upcomingItem = flattenedItems[next.index + 1];
        this.scrollService.scrollToElementById(
          upcomingItem ? upcomingItem.id : 'end-of-list'
        );
      }
    } else {
      this.sectionService.requestSectionExpand(currentSectionId);
      this.scrollService.scrollToElementById('end-of-list');
    }
  }

  async updateItemResolution(
    itemId: string,
    sectionId: string,
    status: ChecklistItemStatus,
    completionTime: string,
    entryTime: string | null,
    advanceToNext = true
  ) {
    const checklist = this.checklist();
    if (!checklist) return;

    await this.itemWriter.updateChecklistItemResolution(
      checklist,
      sectionId,
      itemId,
      status,
      completionTime,
      entryTime
    );
    if (!advanceToNext) return;
    this.moveToNextItem(sectionId, itemId, true);
    if (this.isAllSectionCompleted() && !this.checklistHasRepeatableTasks()) {
      this.state.dialogs.completion.set(true);
    }
  }

  async resetItemStatus(itemId: string, sectionId: string): Promise<void> {
    // "Undo & keep". The server snapshots the completion time into undo_time, so it outranks the
    // re-completion stamp and is not flagged as a manual edit.
    await this.withChecklist(checklist =>
      this.itemWriter.keepUndoChecklistItemContents(
        checklist,
        sectionId,
        itemId
      )
    );
  }

  async clearItemContents(itemId: string, sectionId: string): Promise<void> {
    await this.withChecklist(checklist =>
      this.itemWriter.clearChecklistItemContents(checklist, sectionId, itemId)
    );
  }

  async discardRepeatableItem(
    itemId: string,
    sectionId: string
  ): Promise<void> {
    // Permitted only while the group has more than one live occurrence.
    await this.withChecklist(checklist =>
      this.itemWriter.discardRepeatableChecklistItem(
        checklist,
        sectionId,
        itemId
      )
    );
  }

  /** Live occurrences only because discarded ones are already absent from the model, and the prototype is excluded. */
  repeatableOccurrences(item: ChecklistItem): ChecklistItem[] {
    const groupId = item.repeatableGroupId;
    if (!groupId || item.isRepeatablePrototype) return [];

    const section = this.checklist()?.sections.find(
      candidate => candidate.id === item.sectionId
    );
    return (section?.items ?? []).filter(
      candidate =>
        !candidate.isRepeatablePrototype &&
        (candidate.repeatableGroupId ?? candidate.id) === groupId
    );
  }

  repeatableOccurrenceCount(item: ChecklistItem): number {
    return this.repeatableOccurrences(item).length;
  }

  async fetchServerItem(
    itemId: string,
    sectionId: string
  ): Promise<ChecklistItem | null> {
    return (
      (await this.withChecklist(checklist =>
        this.checklistLoader.fetchServerChecklistItem(
          checklist.id,
          sectionId,
          itemId
        )
      )) ?? null
    );
  }

  async updateItemRemarks(
    itemId: string,
    sectionId: string,
    remarks: string,
    previousRemarks?: string
  ): Promise<string | undefined> {
    return this.withChecklist(checklist =>
      this.itemWriter.updateChecklistItemRemarks(
        checklist,
        sectionId,
        itemId,
        remarks,
        previousRemarks
      )
    );
  }

  async updateItemHeader(
    itemId: string,
    sectionId: string,
    header: string,
    previousHeader?: string
  ): Promise<string | undefined> {
    return this.withChecklist(checklist =>
      this.itemWriter.updateChecklistItemHeader(
        checklist,
        sectionId,
        itemId,
        header,
        previousHeader
      )
    );
  }

  async updateItemCompletionTime(
    itemId: string,
    sectionId: string,
    completionTime: string | null
  ) {
    await this.withChecklist(checklist =>
      this.itemWriter.updateChecklistItemCompletionTime(
        checklist,
        sectionId,
        itemId,
        completionTime
      )
    );
  }

  async updateCustomInputValue(
    itemId: string,
    sectionId: string,
    customInputId: string,
    payload: unknown,
    slotId?: string | null,
    previous?: PreviousCustomInputPayload
  ): Promise<unknown> {
    return this.withChecklist(checklist =>
      this.itemWriter.updateChecklistItemCustomInput(
        checklist,
        sectionId,
        itemId,
        customInputId,
        payload,
        slotId,
        previous
      )
    );
  }

  updateCustomInputPayloadLocally(
    itemId: string,
    customInputId: string,
    payload: unknown,
    slotId?: string | null
  ): void {
    this.withChecklist(checklist =>
      this.itemWriter.updateCustomInputPayloadLocally(
        checklist.id,
        itemId,
        customInputId,
        payload,
        slotId
      )
    );
  }

  private withChecklist<T>(run: (checklist: Checklist) => T): T | undefined {
    const checklist = this.checklist();
    return checklist ? run(checklist) : undefined;
  }

  promoteIfPreview(): void {
    this.promoteIfPreviewAsync().catch(error => {
      if (!(error instanceof IsolatedPreviewWriteError)) {
        throw error;
      }
    });
  }

  // Awaitable so callers (image upload against `checklist().id`) know the backend row exists, and the id survives promotion.
  async promoteIfPreviewAsync(): Promise<void> {
    const checklist = this.checklist();
    if (!checklist || checklist.phase !== 'Preview') return;
    await this.lifecycle.promotePreviewIfNeeded(checklist.id);
  }

  isAllSectionCompleted(): boolean {
    const checklist = this.checklist();
    // A recent-list row carries no sections until it is hydrated, and an empty tree would
    // read as a finished checklist.
    if (!checklist || (checklist.sections ?? []).length === 0) return false;
    const { completedItemCount, totalItemCount } =
      deriveChecklistProgress(checklist);
    return completedItemCount === totalItemCount;
  }

  private checklistHasRepeatableTasks(): boolean {
    return (
      this.checklist()?.sections.some(section =>
        section.items.some(item => item.isRepeatablePrototype)
      ) ?? false
    );
  }

  showDiscardDialog() {
    this.state.dialogs.discard.set(true);
  }

  hideDiscardDialog() {
    this.state.dialogs.discard.set(false);
  }

  showCompleteDialog() {
    this.state.dialogs.completion.set(true);
  }

  hideCompleteDialog() {
    this.state.dialogs.completion.set(false);
  }

  async discardChecklist(): Promise<void> {
    this.hideDiscardDialog();
    return await this.updateChecklistStatus(ChecklistStatus.Discarded);
  }

  async completeChecklist(): Promise<void> {
    this.hideCompleteDialog();
    return await this.updateChecklistStatus(ChecklistStatus.Completed);
  }

  private async updateChecklistStatus(status: ChecklistStatus): Promise<void> {
    await this.withChecklist(checklist =>
      this.lifecycle.updateChecklistStatus(checklist, status)
    );
  }

  /**
   * Item counts for one checklist. A carried section tree is the live copy the user is editing, so
   * it wins. A row without one has only the counts the backend projection stamped on it.
   */
  getChecklistProgress(checklist: Checklist | null): ChecklistProgress {
    if (!checklist || (checklist.sections ?? []).length === 0) {
      return {
        completedItemCount: checklist?.completedItemCount ?? 0,
        totalItemCount: checklist?.totalItemCount ?? 0,
      };
    }
    return deriveChecklistProgress(checklist);
  }
}
